// Le TABLEAU d'un graphique — C1, A10, A11, A27 (plan 12/09).
//
// Il naît de l'alternative textuelle RGAA du 06/08 (`TableauDonnees`, qui vivait repliée
// sous chaque canvas) et la remplace : même construction — les MÊMES `labels` et
// `datasets` que le tracé, le spec EFFECTIF, celui que Chart.js reçoit — mais une vue de
// plein droit, appelée par la bascule, et non plus un dépliant qu'il fallait deviner.
//
// Règle qui ne bouge pas : rien n'est recalculé ni inventé ici. Toute valeur affichée est
// déjà dans le spec. Ce qui s'y ajoute (la valeur absolue d'A10, la part d'un anneau) est
// exactement ce que l'infobulle du graphique montre déjà, lu à la même source.
import type { ChartSpec } from '../../observatoire/ChartBlock'
import { estProportion, fmtFr, fmtPart, libelleCategorie } from './format'

export interface TableauSpecProps {
  spec: ChartSpec
  titre?: string
  /** A11 — séries décochées dans la légende (par libellé) : le tableau les retire aussi. */
  seriesMasquees?: ReadonlySet<string>
  /** A11 sur un anneau : la légende y masque des CATÉGORIES, donc des lignes, pas des colonnes. */
  pointsMasques?: ReadonlySet<string>
  /** A27 — variante PDF : pas de conteneur défilant (rien ne défile sur du papier), typo resserrée. */
  compact?: boolean
}

export function TableauSpec({ spec, titre, seriesMasquees, pointsMasques,
                              compact }: TableauSpecProps) {
  const pyramide = spec.type === 'pyramid'
  const anneau = spec.type === 'doughnut'
  // L'unité d'une série prime sur celle du graphique : un graphique à panneaux (14/10) porte
  // des feux ET des hectares, chaque colonne écrit la sienne.
  const uniteDe = (d: ChartSpec['datasets'][number]) => {
    const u = d.unit ?? spec.unit
    return u ? ` ${u}` : ''
  }

  // Une série entièrement vide n'est pas une colonne : elle n'aurait que des « N/D ».
  const series = spec.datasets
    .filter(d => d.data.some(v => v !== null && v !== undefined))
    .filter(d => !seriesMasquees?.has(String(d.label)))
  const lignes = spec.labels
    .map((l, i) => ({ l, i }))
    .filter(x => !pointsMasques?.has(libelleCategorie(x.l)))

  // Tout décoché : le tableau se tait comme le graphique. Rendre un tableau à zéro colonne
  // serait un cadre vide, pas une information.
  if (lignes.length === 0 || series.length === 0) return null

  // En-tête de la première colonne : « Année » seulement si TOUTES les catégories en sont
  // une (1800-2100) — une courbe peut porter des mois ou des libellés.
  const annees = spec.labels.every(
    l => /^\d{4}$/.test(String(l)) && Number(l) >= 1800 && Number(l) <= 2100)

  const cellule = (d: ChartSpec['datasets'][number], i: number) => {
    const v = d.data[i]
    if (v === null || v === undefined) return <span className="obs2-nd-tab">N/D</span>
    // Pyramide : les hommes sont servis négatifs pour le miroir — on affiche la valeur
    // absolue, comme le fait déjà l'infobulle.
    const principal = `${fmtFr(pyramide ? Math.abs(v) : v)}${uniteDe(d)}`

    // ── A10 — la valeur ABSOLUE à côté de la proportion ───────────────────────────────
    // « Les tableaux montrent les valeurs absolues à côté des proportions dès que le
    // graphique les possède. » Le graphique les possède dans `abs` / `absLabel` : c'est
    // le champ que l'infobulle lit déjà pour écrire « 13,9 % · 1 234 logements ». Le
    // tableau lit LE MÊME champ — il ne redivise rien, une division maison aurait pu
    // rendre un nombre différent de celui qu'on lit au survol, sur la même donnée.
    const a = d.abs?.[i]
    if (a !== null && a !== undefined) {
      return <>{principal}<span className="obs2-tab-abs">{fmtFr(a)} {d.absLabel || 'faits'}</span></>
    }

    // Anneau à valeurs ABSOLUES : la valeur tracée est un décompte, et c'est la part qui
    // manque au lecteur (« 12 sur combien ? »). On rend ce que l'infobulle rend déjà.
    //
    // ⚠️ Jamais quand l'unité est déjà une proportion (revue codex P2, 12/09). Sur
    // « Répartition du territoire par niveau d'aléa », dont les données SONT des
    // pourcentages, ce calcul divisait des parts arrondies par la somme de ces arrondis et
    // affichait un second pourcentage FABRIQUÉ, qui peut différer du chiffre servi. La
    // doctrine du dépôt est sans ambiguïté : on n'affiche que la donnée servie.
    if (anneau && !estProportion(spec.unit)) {
      const total = (d.data as (number | null)[]).reduce<number>((s, x) => s + (x ?? 0), 0)
      if (total) return <>{principal}<span className="obs2-tab-abs">{fmtPart(v, total)} %</span></>
    }
    return principal
  }

  const table = (
    <table>
      <caption className="sr-only">
        {titre ? `Données du graphique « ${titre} »` : 'Données du graphique'}
      </caption>
      <thead>
        <tr>
          <th scope="col">{pyramide ? "Tranche d'âge" : annees ? 'Année' : 'Catégorie'}</th>
          {series.map((d, i) => <th key={i} scope="col">{d.label}</th>)}
        </tr>
      </thead>
      <tbody>
        {lignes.map(({ l, i }) => (
          <tr key={i}>
            {/* A13 : une catégorie sans libellé est NOMMÉE, jamais rendue « null ». */}
            <th scope="row">{libelleCategorie(l)}</th>
            {series.map((d, j) => <td key={j}>{cellule(d, i)}</td>)}
          </tr>
        ))}
      </tbody>
    </table>
  )

  // A27 : sur la route print, le tableau se pose SOUS le graphique et doit tenir sur la
  // page — aucun conteneur à défilement, qui sur papier coupe simplement les lignes.
  return compact
    ? <div className="obs2-chart-data obs2-chart-data-print">{table}</div>
    : <div className="obs2-chart-data"><div className="obs2-chart-data-scroll">{table}</div></div>
}
